/**
 * SignalEditor Component
 *
 * Main component for editing a single signal.
 * Combines SignalSelector, parameter editor, copy/paste actions, and validation state.
 * Layout: [pencil] [copy/paste] [dropdown] [summary] [x]
 */

import React, { useState, useRef, useEffect } from "react";
import { ClipboardPaste, Copy, Pencil, X } from "lucide-react";
import { SignalSelector } from "./SignalSelector";
import { SignalParamPopover } from "./SignalParamPopover";
import { ConfiguredSignal, SIGNAL_REGISTRY, isSignalComplete, getSignalSummary } from "../../../../../config/signals";
import { InfoTooltip } from "../../../../../components/common/InfoTooltip";
import styles from "./SignalEditor.module.css";
import logger from '../../../../../logging/logger';
import type { CopiedSignalPayload } from "./types";

interface SignalEditorProps {
  signal: ConfiguredSignal;
  category: "entry" | "exit";
  onUpdate: (signal: ConfiguredSignal) => void;
  onRemove: () => void;
  allowedSignalTypes?: string[];
  copiedSignal: CopiedSignalPayload | null;
  onCopySignal: (signal: CopiedSignalPayload) => void;
}

// Known invalid signal types that should never be displayed
const INVALID_SIGNAL_TYPES = new Set(["Option", "Stock", "Future"]);
const cloneSignalParams = (params: Record<string, unknown>): Record<string, unknown> => {
  if (typeof structuredClone === "function") {
    return structuredClone(params);
  }

  try {
    return JSON.parse(JSON.stringify(params)) as Record<string, unknown>;
  } catch {
    return { ...params };
  }
};

const isSignalTypeAllowed = (
  signalType: string,
  category: "entry" | "exit",
  allowedSignalTypes?: string[],
): boolean => {
  const schema = SIGNAL_REGISTRY[signalType];
  if (!schema) return false;
  if (schema.category !== "both" && schema.category !== category) return false;
  if (allowedSignalTypes && !allowedSignalTypes.includes(signalType)) return false;
  return true;
};

export const SignalEditor: React.FC<SignalEditorProps> = ({
  signal,
  category,
  onUpdate,
  onRemove,
  allowedSignalTypes,
  copiedSignal,
  onCopySignal,
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const pencilRef = useRef<HTMLButtonElement>(null);

  // Skip rendering entirely for invalid signal types - auto-remove on mount
  const isInvalidType = INVALID_SIGNAL_TYPES.has(signal.type);
  useEffect(() => {
    if (isInvalidType) {
      logger.warn(`[SignalEditor] Auto-removing invalid signal type: ${signal.type}`);
      onRemove();
    }
  }, [isInvalidType, signal.type, onRemove]);

  // Auto-open popover when signal type changes to one with params (and params are empty)
  const prevTypeRef = useRef(signal.type);
  useEffect(() => {
    if (
      signal.type &&
      signal.type !== prevTypeRef.current &&
      Object.keys(signal.params).length === 0
    ) {
      const newSchema = SIGNAL_REGISTRY[signal.type];
      if (newSchema && Object.keys(newSchema.params).length > 0) {
        // Delay to ensure pencil button is rendered and click-outside listener
        // in SignalParamPopover (setTimeout(0)) doesn't immediately close it
        const timer = setTimeout(() => setShowPopover(true), 50);
        prevTypeRef.current = signal.type;
        return () => clearTimeout(timer);
      }
    }
    prevTypeRef.current = signal.type;
  }, [signal.type, signal.params]);

  // Don't render invalid signal types
  if (isInvalidType) {
    return null;
  }

  const schema = signal.type ? SIGNAL_REGISTRY[signal.type] : undefined;
  const hasParams = schema && Object.keys(schema.params).length > 0;
  const isComplete = isSignalComplete(signal);
  const summary = getSignalSummary(signal);

  const handleTypeChange = (newType: string) => {
    onUpdate({
      ...signal,
      type: newType,
      params: {}, // Reset params when type changes
    });
  };

  const handleParamsChange = (params: Record<string, unknown>) => {
    onUpdate({ ...signal, params });
  };
  const canCopy = signal.type.trim() !== "" && Boolean(SIGNAL_REGISTRY[signal.type]);
  const canPaste = Boolean(
    copiedSignal && isSignalTypeAllowed(copiedSignal.type, category, allowedSignalTypes)
  );

  const handleCopy = () => {
    if (!canCopy) return;
    onCopySignal({
      type: signal.type,
      params: cloneSignalParams(signal.params),
    });
  };

  const handlePaste = () => {
    if (!copiedSignal || !canPaste) return;
    onUpdate({
      ...signal,
      type: copiedSignal.type,
      params: cloneSignalParams(copiedSignal.params),
    });
  };

  // Determine pencil button state
  const getPencilState = (): "valid" | "invalid" | "none" => {
    if (!signal.type) return "none";
    if (!hasParams) return "none";
    return isComplete ? "valid" : "invalid";
  };

  const pencilState = getPencilState();

  return (
    <div className={styles.container}>
      {/* Pencil area - fixed width to prevent layout shift */}
      {pencilState !== "none" ? (
        <div className={styles.pencilWrapper}>
          <button
            ref={pencilRef}
            className={`${styles.pencilButton} ${styles[pencilState]}`}
            onClick={() => setShowPopover(!showPopover)}
            aria-label="Edit parameters"
          >
            <Pencil />
          </button>

          {/* Popover - rendered via portal */}
          {showPopover && (
            <SignalParamPopover
              signalType={signal.type}
              params={signal.params}
              onParamsChange={handleParamsChange}
              onClose={() => setShowPopover(false)}
              anchorRef={pencilRef}
            />
          )}
        </div>
      ) : (
        /* Empty spacer when no pencil to maintain consistent layout */
        <div className={styles.pencilSpacer} />
      )}

      <div className={styles.actionsWrapper}>
        <button
          className={`${styles.actionButton} ${styles.copyButton}`}
          onClick={handleCopy}
          aria-label="Copy signal"
          title={canCopy ? "Copy signal and parameters" : "Select a signal before copying"}
          disabled={!canCopy}
        >
          <Copy />
        </button>
        <button
          className={`${styles.actionButton} ${styles.pasteButton}`}
          onClick={handlePaste}
          aria-label="Paste signal"
          title={canPaste ? "Paste copied signal and parameters" : "No compatible copied signal to paste"}
          disabled={!canPaste}
        >
          <ClipboardPaste />
        </button>
      </div>

      {/* Signal selector dropdown - wrapped for consistent width */}
      <div className={styles.selectorWrapper}>
        <SignalSelector
          value={signal.type}
          onChange={handleTypeChange}
          category={category}
          allowedSignalTypes={allowedSignalTypes}
        />
      </div>

      {/* Signal info tooltip */}
      {schema?.description && (
        <InfoTooltip text={schema.description} />
      )}

      {/* Summary preview */}
      {summary && (
        <span className={styles.summary} title={summary}>
          {summary}
        </span>
      )}

      {/* Remove button */}
      <button
        className={styles.removeButton}
        onClick={onRemove}
        aria-label="Remove signal"
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </div>
  );
};
